import React, { useEffect, useState, useRef } from 'react';
import Cookies from 'js-cookie';
import './style.css';

// API URL configuration
// Dev (bun run start): use local server; Build: use production Render URL
const HARDCODED_API = process.env.NODE_ENV === 'development'
    ? "http://localhost:8080"
    : "https://kruvonicsitev2.onrender.com";
const API_URL = HARDCODED_API;

// Temporary debugging flags for uploads
const DEBUG_UPLOAD = true; // set to false after debugging

// For ngrok: Set REACT_APP_API_URL environment variable to your ngrok URL
// Example: REACT_APP_API_URL=https://abc123.ngrok.io
// For IP access: The app will automatically use your IP address

const KVARTAL_NAMES = ['І квартал', 'ІІ квартал', 'ІІІ квартал', 'ІV квартал'];
const getCurrentYear = () => new Date().getFullYear();

const getKvartalDateRange = (year, kvartalIndex, cfg) => {
    if (!KVARTAL_NAMES[kvartalIndex]) return null;
    const key = `${String(year)}-${kvartalIndex}`;
    const override = cfg?.kvartalOverrides?.[key];

    if (override?.start && override?.end) {
        const parseDDMMYY = (str) => {
            const parts = str.split('.');
            if (parts.length !== 3) return null;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            let y = parseInt(parts[2], 10);
            if (y < 100) y += 2000;
            const d = new Date(y, month, day);
            if (d.getDate() !== day || d.getMonth() !== month) return null;
            return d;
        };
        const start = parseDDMMYY(override.start);
        const end = parseDDMMYY(override.end);
        if (start && end) return { start, end, overridden: true };
    }

    const startMonth = kvartalIndex * 3;
    const endMonth = startMonth + 2;
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, endMonth + 1, 0);
    return { start, end, overridden: false };
};

const getCurrentKvartalIndex = (cfg) => {
    const now = new Date();
    const year = now.getFullYear();
    for (let i = KVARTAL_NAMES.length - 1; i >= 0; i--) {
        const range = getKvartalDateRange(year, i, cfg);
        if (range && now >= range.start && now <= range.end) return i;
    }
    return Math.floor(now.getMonth() / 3);
};

const getCurrentKvartalName = (cfg) => KVARTAL_NAMES[Math.min(getCurrentKvartalIndex(cfg), KVARTAL_NAMES.length - 1)];

const parseKvartalIndex = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        if (value >= 0 && value <= 3) return value;
        if (value >= 1 && value <= 4) return value - 1;
    }

    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const exactNameIndex = KVARTAL_NAMES.indexOf(trimmed);
    if (exactNameIndex !== -1) return exactNameIndex;

    const digitMatch = trimmed.match(/[1-4]/);
    if (digitMatch) return Number(digitMatch[0]) - 1;

    const normalized = trimmed
        .toLowerCase()
        .replace(/квартал/g, '')
        .replace(/\s+/g, '');

    const romanMap = {
        i: 0,
        ii: 1,
        iii: 2,
        iv: 3,
        і: 0,
        іі: 1,
        ііі: 2,
        іv: 3
    };

    return romanMap[normalized] ?? null;
};

const getConfiguredStartKvartalIndex = (config) => {
    if (!config || typeof config !== 'object') return null;
    const candidates = [
        config.startKvartal,
        config.startQuarter,
        config.startKvartalNumber,
        config.startQuarterNumber,
        config.startKvartalIndex,
        config.startQuarterIndex,
        config.start_kvartal,
        config.start_quarter,
        config.start_kvartal_number,
        config.start_quarter_number,
        config.start_kvartal_index,
        config.start_quarter_index
    ];

    for (const candidate of candidates) {
        const parsed = parseKvartalIndex(candidate);
        if (parsed !== null) return parsed;
    }

    return null;
};

const getAvailableKvartalsForYear = (selectedYear, config) => {
    const selectedYearNum = parseInt(selectedYear, 10);
    const currentYear = getCurrentYear();
    const currentKvIndex = getCurrentKvartalIndex(config);
    const maxIndex = selectedYearNum < currentYear ? KVARTAL_NAMES.length - 1 : currentKvIndex;

    let minIndex = 0;
    const configStartYear = Number(config?.startYear);
    if (Number.isFinite(configStartYear) && selectedYearNum === configStartYear) {
        const configStartKvIndex = getConfiguredStartKvartalIndex(config);
        if (configStartKvIndex !== null) {
            minIndex = Math.max(0, Math.min(configStartKvIndex, KVARTAL_NAMES.length - 1));
        }
    }

    if (minIndex > maxIndex) return [];
    return KVARTAL_NAMES.slice(minIndex, maxIndex + 1);
};

const normalizeNamesPayload = (payload) => {
    const source = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.documents)
            ? payload.documents
            : Array.isArray(payload?.data)
                ? payload.data
                : [];

    return source
        .map((entry) => {
            const name = String(entry?.name || entry?.fullName || entry?.username || '').trim();
            if (!name) return null;

            const pilgaRaw = entry?.pilga ?? entry?.benefit ?? entry?.privilege;
            const pilga = typeof pilgaRaw === 'string' && pilgaRaw.trim() ? pilgaRaw.trim() : 'Звичайна';

            return {
                ...entry,
                name,
                pilga
            };
        })
        .filter(Boolean);
};

const normalizeNameForLookup = (value) => String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const alignTableRowsWithNames = (rows, names, adminInfo) => {
    const list = Array.isArray(rows) ? rows : [];
    const namesList = Array.isArray(names) ? names : [];

    const canonicalByKey = new Map(
        namesList
            .filter(entry => entry && entry.name)
            .map(entry => [normalizeNameForLookup(entry.name), entry.name])
    );

    const orderedNameKeys = namesList
        .filter(entry => entry && entry.name)
        .map(entry => normalizeNameForLookup(entry.name));
    const orderByNameKey = new Map(orderedNameKeys.map((key, index) => [key, index]));

    const allowedKeys = Number(adminInfo?.level) === 1
        ? new Set((Array.isArray(adminInfo?.allowedNames) ? adminInfo.allowedNames : []).map(normalizeNameForLookup))
        : null;

    const alignedRows = list
        .map(row => {
            if (!row || !row.name) return null;
            const key = normalizeNameForLookup(row.name);
            const canonicalName = canonicalByKey.get(key) || row.name;
            return { ...row, name: canonicalName };
        })
        .filter(Boolean)
        .filter(row => {
            if (!allowedKeys) return true;
            return allowedKeys.has(normalizeNameForLookup(row.name));
        });

    return alignedRows.sort((a, b) => {
        const aKey = normalizeNameForLookup(a.name);
        const bKey = normalizeNameForLookup(b.name);
        const aOrder = orderByNameKey.has(aKey) ? orderByNameKey.get(aKey) : Number.MAX_SAFE_INTEGER;
        const bOrder = orderByNameKey.has(bKey) ? orderByNameKey.get(bKey) : Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.name.localeCompare(b.name, 'uk', { sensitivity: 'base' });
    });
};

const ZIP_STATUS_LABELS = {
    pending: 'Очікування...',
    running: 'Створення архіву...',
    completed: 'Готово',
    error: 'Помилка',
    aborted: 'Скасовано'
};

const getZipStatusLabel = (status) => ZIP_STATUS_LABELS[status] || 'Опрацювання...';

// This component remains unchanged, it was working correctly.
const PaymentInterface = ({ user, year, kvartal }) => {
    const [selectedPaymentType, setSelectedPaymentType] = useState('Курінна');
    const [file, setFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('');
    const [flashMessage, setFlashMessage] = useState(''); // повідомлення після перезавантаження
    const fileInputRef = useRef(null);
    const [paymentStatus, setPaymentStatus] = useState({ isLoading: true, amountLeft: 0 });
    const [copyMsg, setCopyMsg] = useState('');

    useEffect(() => {
        if (!user || !API_URL) return;
        const fetchStatus = async () => {
            setPaymentStatus({ isLoading: true, amountLeft: 0 });
            try {
                const res = await fetch(`${API_URL}/status/${user.name}/${year}/${kvartal}/${user.pilga}/${selectedPaymentType}`);
                if (!res.ok) throw new Error('Failed to fetch status');
                const data = await res.json();
                setPaymentStatus({ isLoading: false, amountLeft: data.amountLeft });
            } catch (error) { 
                console.error("Error fetching payment status:", error);
                setPaymentStatus({ isLoading: false, amountLeft: NaN }); 
            }
        };
        fetchStatus();
    }, [user, year, kvartal, selectedPaymentType]);

    useEffect(() => {
        // Показуємо повідомлення успіху після перезавантаження сторінки
        const flag = localStorage.getItem('lastUploadSuccess');
        if (flag === '1') {
            setFlashMessage('Оплату успішно додано. Дякуємо!');
            localStorage.removeItem('lastUploadSuccess');
        }
    }, []);

    // Keep-alive ping: call /ping on mount and every 15 minutes
    useEffect(() => {
        if (!API_URL) return;
        const ping = () => { fetch(`${API_URL}/ping`).catch(() => {}); };
        ping();
        const id = setInterval(ping, 15 * 60 * 1000);
        return () => clearInterval(id);
    }, []);

    const handleUpload = async () => {
        if (!file || !user) { setUploadStatus('Оберіть файл'); return; }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('username', user.name);
        formData.append('year', year);
        formData.append('kvartal', kvartal);
        formData.append('pilga', user.pilga);
        formData.append('paymentType', selectedPaymentType);
        setUploadStatus('Завантаження та обробка...');

        const sep = API_URL.includes('?') ? '&' : '?';
        const debugQs = DEBUG_UPLOAD ? `${sep}llm=1&debug=1` : '';

        const attempt = async (tryNo = 1, maxTries = 3) => {
            try {
                const res = await fetch(`${API_URL}/upload${debugQs}`, { method: 'POST', body: formData });
                const result = await res.json().catch(() => ({}));

                // Server suggests retry when LLM is still parsing
                if (res.status === 202 && result?.llmRetrySuggested) {
                    if (tryNo < maxTries) {
                        setUploadStatus('Ще пару секунд... (спроба ' + (tryNo + 1) + ' з ' + maxTries + ')');
                        await new Promise(r => setTimeout(r, 2000));
                        return attempt(tryNo + 1, maxTries);
                    }
                    throw new Error(result.message || 'Не вдалося обробити. Спробуйте ще раз.');
                }

                if (!res.ok) throw new Error(result.message || 'Upload failed');

                if (DEBUG_UPLOAD) {
                    console.log('[UPLOAD DEBUG RESULT]', result);
                    if (result.llm) console.log('[LLM JSON]', result.llm);
                    if (result.debug) console.log('[SERVER DEBUG]', result.debug);
                }

                setUploadStatus('Файл успішно завантажено! Оновлення...');
                localStorage.setItem('lastUploadSuccess', '1');
                setTimeout(() => window.location.reload(), 1000);
            } catch (error) {
                setUploadStatus(`Помилка завантаження: ${error.message}`);
            }
        };

        attempt();
    };

    const renderPrice = () => {
        if (paymentStatus.isLoading) return 'Завантаження...';
        if (isNaN(paymentStatus.amountLeft)) return 'Помилка';
        if (paymentStatus.amountLeft <= 0) return 'Сплачено!';
        return `${paymentStatus.amountLeft.toFixed(2)} грн`;
    };

    // Шаблон призначення платежу (для копіювання)
    const templateText = `Внесок: ${selectedPaymentType}; За: ${kvartal}, ${year}; ПІБ: ${user?.name || ''}`;

    return (
        <section style={{ background: 'white', padding: '20px', borderRadius: '6px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', textAlign: 'center', marginTop: '15px' }}>
            <ul style={{ display: 'flex', gap: '10px', padding: 0, listStyle: 'none', margin: '0 0 20px 0' }}>
                {['Курінна', 'Станична', 'Уладова'].map(type => ( <li key={type} style={{ flex: 1 }}> <button onClick={() => setSelectedPaymentType(type)} style={{ background: selectedPaymentType === type ? '#782C36' : '#eee', color: selectedPaymentType === type ? 'white' : '#333', cursor: 'pointer', padding: '10px', borderRadius: '4px', border: 'none', width: '100%', fontSize: '16px' }}>{type}</button> </li> ))}
            </ul>
            <h1 className="price" style={{ fontSize: '48px', marginBottom: '20px' }}>{renderPrice()}</h1>
            <div onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]) }} onDragOver={e => e.preventDefault()} onClick={() => fileInputRef.current.click()} style={{ border: '2px dashed #aaa', borderRadius: '6px', padding: '40px', background: '#fafafa', cursor: 'pointer', marginBottom: '20px' }}>
                {file ? <p>Файл готовий: {file.name}</p> : <p>Перетягніть файл сюди або клікніть</p>}
                <input type="file" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} ref={fileInputRef} />
            </div>
            <button onClick={handleUpload} style={{ background: '#782C36', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px', width: '100%' }}>Завантажити файл</button>
            {uploadStatus && <p style={{ marginTop: '10px' }}>{uploadStatus}</p>}
            {flashMessage && <p style={{ marginTop: '10px', color: 'green', fontWeight: '600' }}>{flashMessage}</p>}

            {/* Реквізити та шаблон опису платежу */}
            <div style={{ textAlign: 'left', marginTop: '24px', padding: '16px', border: '1px solid #eee', borderRadius: '8px', background: '#fafafa' }}>
                <h3 style={{ marginTop: 0, marginBottom: '10px', color: '#333' }}>Реквізити для оплати</h3>
                {selectedPaymentType === 'Курінна' && (
                    <div style={{ marginBottom: '12px' }}>
                        <p style={{ margin: '6px 0', color: '#444' }}>Курінна: оплата через Monobank Банку</p>
                        <a href="https://send.monobank.ua/jar/2BDCKNHLX3" target="_blank" rel="noreferrer" style={{ color: '#782C36', fontWeight: 600 }}>Відкрити банку Monobank</a>
                    </div>
                )}
                {selectedPaymentType === 'Станична' && (
                    <div style={{ marginBottom: '12px' }}>
                        <p style={{ margin: '6px 0', color: '#444' }}>Станична: оплата за реквізитами </p>
                        <a href="https://bank.gov.ua/qr/QkNECjAwMgoyClVDVAoKw84gysivwtHcysjJIM_LwNHSClVBODIzMDUyOTkwMDAwMDI2MDAxMDQ1MDE2ODc5ClVBSDE2MC4wCjI2MzQ1MTEwCgoKwevg4-7ks-nt6Okg4u3l8e7qIO3gIPHy4PLz8u3zIOSz_-v87bPx8vwK" target="_blank" rel="noreferrer" style={{ color: '#782C36', fontWeight: 600 }}>Лінк на платіж.</a>
                        <p style={{ margin: '6px 0', color: '#444' }}>Реквізит IBAN </p>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={async () => {
                                    const cardDisplay = 'UA823052990000026001045016879';
                                    const cardCopy = cardDisplay.replace(/\s+/g, '');
                                    try {
                                        await navigator.clipboard.writeText(cardCopy);
                                        setCopyMsg('Реквізити скопійовано');
                                        setTimeout(() => setCopyMsg(''), 1500);
                                    } catch (_) {
                                        setCopyMsg('Скопіюйте вручну:UA823052990000026001045016879');
                                        setTimeout(() => setCopyMsg(''), 2000);
                                    }
                                }}
                                onKeyDown={async (e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                                style={{ background: '#fff', padding: '6px 8px', borderRadius: '6px', border: '1px solid #e0e0e0', cursor: 'pointer', userSelect: 'none' }}
                                title="Натисніть, щоб скопіювати номер картки"
                            >
                                UA823052990000026001045016879
                            </span>
                        </div>
                    </div>
                )}
                {selectedPaymentType === 'Уладова' && (
                    <div style={{ marginBottom: '12px' }}>
                        <p style={{ margin: '6px 0', color: '#444' }}>Уладова: оплата на картку</p>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={async () => {
                                    const cardDisplay = '4874070054018385';
                                    const cardCopy = cardDisplay.replace(/\s+/g, '');
                                    try {
                                        await navigator.clipboard.writeText(cardCopy);
                                        setCopyMsg('Номер картки скопійовано');
                                        setTimeout(() => setCopyMsg(''), 1500);
                                    } catch (_) {
                                        setCopyMsg('Скопіюйте вручну:4874070054018385');
                                        setTimeout(() => setCopyMsg(''), 2000);
                                    }
                                }}
                                onKeyDown={async (e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                                style={{ background: '#fff', padding: '6px 8px', borderRadius: '6px', border: '1px solid #e0e0e0', cursor: 'pointer', userSelect: 'none' }}
                                title="Натисніть, щоб скопіювати номер картки"
                            >
                                4874070054018385
                            </span>
                        </div>
                    </div>
                )}

                <h4 style={{ marginBottom: '6px', color: '#333' }}>Шаблон призначення платежу</h4>
                <p style={{ margin: '6px 0', color: '#444' }}>Скопіюйте та вставте цей опис при оплаті:</p>
                <div style={{ display: 'grid', gap: '8px' }}>
                    <input
                        readOnly
                        value={templateText}
                        title="Натисніть, щоб скопіювати"
                        style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', fontFamily: 'inherit', cursor: 'pointer' }}
                        onClick={async (e) => {
                            try {
                                await navigator.clipboard.writeText(templateText);
                                setCopyMsg('Скопійовано у буфер обміну');
                                setTimeout(() => setCopyMsg(''), 1500);
                            } catch (_) {
                                // Фолбек: виділення тексту
                                e.target.select();
                                setCopyMsg('Скопіюйте вручну (Ctrl/Cmd + C)');
                                setTimeout(() => setCopyMsg(''), 2000);
                            }
                        }}
                        onFocus={(e) => e.target.select()}
                    />
                    {copyMsg && <span style={{ color: '#2e7d32', fontSize: '12px' }}>{copyMsg}</span>}
                </div>
            </div>
        </section>
    );
};

// This component remains unchanged.
const AdminTable = ({ adminInfo, names }) => {
    const [year, setYear] = useState(() => getCurrentYear());
    const [kvartal, setKvartal] = useState(() => getCurrentKvartalName());
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [config, setConfig] = useState(null);
    const [availableYears, setAvailableYears] = useState([]);
    const [availableKvartals, setAvailableKvartals] = useState([]);
    const [expandedName, setExpandedName] = useState(null);
    const [receiptsCache, setReceiptsCache] = useState({});
    const [isReceiptsLoading, setIsReceiptsLoading] = useState(false);
    const [receiptsError, setReceiptsError] = useState('');
    const [isDownloadingZip, setIsDownloadingZip] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [zipProgress, setZipProgress] = useState(null);
    const progressPollRef = useRef(null);
    const activeProgressIdRef = useRef(null);

    // Debt view state
    const [viewMode, setViewMode] = useState('contributions'); // 'contributions' | 'debt'
    const [debtAll, setDebtAll] = useState(true);
    const [debtFromYear, setDebtFromYear] = useState(() => getCurrentYear());
    const [debtFromKvartal, setDebtFromKvartal] = useState('І квартал');
    const [debtToYear, setDebtToYear] = useState(() => getCurrentYear());
    const [debtToKvartal, setDebtToKvartal] = useState(() => getCurrentKvartalName());
    const [debtData, setDebtData] = useState(null);
    const [debtLoading, setDebtLoading] = useState(false);
    const [debtError, setDebtError] = useState('');
    const [expandedDebtName, setExpandedDebtName] = useState(null);
    const allKvartalsList = KVARTAL_NAMES;
    useEffect(() => {
        if (!API_URL) return;
        fetch(`${API_URL}/config`)
            .then(res => res.json())
            .then(data => setConfig(data))
            .catch(err => console.error("Could not fetch config.json.", err));
    }, []);

    useEffect(() => {
        if (!config) return;
        const currentYearVal = getCurrentYear();
        const years = Array.from({ length: currentYearVal - config.startYear + 1 }, (_, i) => config.startYear + i);
        setAvailableYears(years);
    }, [config]);

    useEffect(() => {
        if (!config) return;
        const overriddenCurrent = getCurrentKvartalName(config);
        setKvartal(overriddenCurrent);
        const kvartalsForYear = getAvailableKvartalsForYear(year, config);
        setAvailableKvartals(kvartalsForYear);
    }, [config]);

    useEffect(() => {
        if (!config) return;
        const kvartalsForYear = getAvailableKvartalsForYear(year, config);
        setAvailableKvartals(kvartalsForYear);

        if (!kvartalsForYear.includes(kvartal) && kvartalsForYear.length) {
            setKvartal(kvartalsForYear[kvartalsForYear.length - 1]);
        }
    }, [year]);

    useEffect(() => {
        if (!config || !API_URL) return;
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const res = await fetch(`${API_URL}/table-data?year=${year}&kvartal=${kvartal}`);
                if (!res.ok) throw new Error('Failed to fetch table data');
                const data = await res.json();
                setTableData(alignTableRowsWithNames(data, names, adminInfo));
            } catch (error) {
                console.error('Failed to fetch table data', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [year, kvartal, adminInfo, config, names]);

    // Fetch accumulated debt across a quarter range
    useEffect(() => {
        if (viewMode !== 'debt' || !API_URL) return;
        let cancelled = false;
        const params = new URLSearchParams();
        if (debtAll) {
            params.set('all', '1');
        } else {
            params.set('fromYear', String(debtFromYear));
            params.set('fromKvartal', debtFromKvartal);
            params.set('toYear', String(debtToYear));
            params.set('toKvartal', debtToKvartal);
        }
        setDebtLoading(true);
        setDebtError('');
        setExpandedDebtName(null);
        fetch(`${API_URL}/admin/debts?${params.toString()}`)
            .then(r => { if (!r.ok) return Promise.reject(new Error('Не вдалося отримати борги.')); return r.json(); })
            .then(data => { if (!cancelled) setDebtData(data); })
            .catch(err => { if (!cancelled) setDebtError(err.message || 'Помилка.'); })
            .finally(() => { if (!cancelled) setDebtLoading(false); });
        return () => { cancelled = true; };
    }, [viewMode, debtAll, debtFromYear, debtFromKvartal, debtToYear, debtToKvartal, API_URL]);

    const toggleDebtUser = (name) => setExpandedDebtName(prev => (prev === name ? null : name));

    // Helper to refresh table after actions (e.g., upload)
    const refreshTableData = async () => {
        if (!config || !API_URL) return;
        try {
            const res = await fetch(`${API_URL}/table-data?year=${year}&kvartal=${kvartal}`);
            if (!res.ok) return;
            const data = await res.json();
            setTableData(alignTableRowsWithNames(data, names, adminInfo));
        } catch (_) {}
    };

    useEffect(() => {
        setExpandedName(null);
        setReceiptsError('');
        setReceiptsCache({});
    }, [year, kvartal]);

    useEffect(() => {
        return () => {
            if (progressPollRef.current) {
                clearInterval(progressPollRef.current);
            }
            activeProgressIdRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!zipProgress || zipProgress.status !== 'completed') return;
        const timer = setTimeout(() => setZipProgress(null), 4000);
        return () => clearTimeout(timer);
    }, [zipProgress]);

    const canDownloadZip = Number(adminInfo?.level) === 2;
    const kvartал = kvartal; // alias for legacy Cyrillic variable usage in JSX

    const stopProgressPolling = () => {
        if (progressPollRef.current) {
            clearInterval(progressPollRef.current);
            progressPollRef.current = null;
        }
        activeProgressIdRef.current = null;
    };

    const fetchZipProgress = async (progressId) => {
        if (!API_URL || !progressId) return;
        if (activeProgressIdRef.current !== progressId) return;
        try {
            const resp = await fetch(`${API_URL}/admin/r2/download-progress/${progressId}`);
            if (!resp.ok) {
                if (resp.status === 404) {
                    setZipProgress(prev => prev && prev.id === progressId
                        ? { ...prev, status: 'error', error: 'Прогрес не знайдено.' }
                        : prev);
                    stopProgressPolling();
                }
                return;
            }
            const data = await resp.json();
            if (activeProgressIdRef.current !== progressId) return;
            setZipProgress(data);
            if (data && ['completed', 'error', 'aborted'].includes(data.status)) {
                stopProgressPolling();
            }
        } catch (error) {
            console.error('Failed to fetch zip progress', error);
            if (activeProgressIdRef.current !== progressId) return;
            setZipProgress(prev => prev && prev.id === progressId
                ? { ...prev, status: 'error', error: 'Не вдалося оновити прогрес.' }
                : prev);
            stopProgressPolling();
        }
    };

    const startZipProgressPolling = (progressId) => {
        if (!progressId) return;
        stopProgressPolling();
        activeProgressIdRef.current = progressId;
        fetchZipProgress(progressId);
        progressPollRef.current = setInterval(() => fetchZipProgress(progressId), 1500);
    };

    const handleDownloadZip = async () => {
        if (!API_URL || !adminInfo?.name || !year || !kvartal) return;
        setDownloadError('');
        setIsDownloadingZip(true);
        stopProgressPolling();
        setZipProgress(null);
        try {
            const params = new URLSearchParams({ requester: adminInfo.name, year, kvartal });
            const res = await fetch(`${API_URL}/admin/r2/download?${params.toString()}`);
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload.message || 'Не вдалося завантажити архів.');
            }
            const progressId = res.headers.get('X-Zip-Progress-Id');
            const totalHint = parseInt(res.headers.get('X-Zip-Total-Files') || '', 10);
            if (progressId) {
                setZipProgress({
                    id: progressId,
                    status: 'running',
                    processed: 0,
                    total: Number.isFinite(totalHint) ? totalHint : null,
                    label: `${year}_${kvartal}`.replace(/\s+/g, '_'),
                    error: null,
                    lastFile: null
                });
                startZipProgressPolling(progressId);
            }
            const blob = await res.blob();
            const fileName = `${year}_${kvartal}`.replace(/\s+/g, '_');
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${fileName}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setDownloadError(err.message || 'Не вдалося завантажити архів.');
            stopProgressPolling();
            setZipProgress(prev => prev ? { ...prev, status: 'error', error: err.message || 'Не вдалося завантажити архів.' } : prev);
        } finally {
            setIsDownloadingZip(false);
        }
    };

    // ...
    const ensureReceiptsFor = async (name) => {
        if (!adminInfo?.name) {
            setReceiptsError('Інформація про адміністратора недоступна.');
            return;
        }
        if (!API_URL) {
            setReceiptsError('API URL не налаштовано.');
            return;
        }
        if (receiptsCache[name]) {
            return;
        }

        setIsReceiptsLoading(true);
        setReceiptsError('');
        try {
            const qs = new URLSearchParams({
                username: name,
                year,
                kvartal,
                requester: adminInfo.name
            });
            const res = await fetch(`${API_URL}/receipts?${qs.toString()}`);
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload.message || 'Не вдалося отримати квитанції.');
            }
            const data = await res.json();
            setReceiptsCache(prev => ({ ...prev, [name]: data.items || [] }));
        } catch (err) {
            setReceiptsError(err.message || 'Не вдалося отримати квитанції.');
        } finally {
            setIsReceiptsLoading(false);
        }
    };

    const handleToggleReceipts = async (name) => {
        if (expandedName === name) {
            setExpandedName(null);
            setReceiptsError('');
            return;
        }
        setExpandedName(name);
        setReceiptsError('');
        await ensureReceiptsFor(name);
    };

    // Helper to resolve receipt URL (absolute vs relative)
    const resolveReceiptUrl = (url) => {
        if (!url) return '#';
        return /^https?:\/\//i.test(url) ? url : `${API_URL}${url}`;
    };

    // Admin upload from this table has been removed in favor of a separate AdminSend page

    // Delete a receipt (calls server DELETE /receipts)
    const deleteReceipt = async (name, rec) => {
        if (!window.confirm('Видалити цю квитанцію?')) return;
        try {
            const payload = {
                username: name,
                year: rec.year,
                kvartal: rec.kvartal,
                paymentType: rec.paymentType,
                tripleKey: rec.tripleKey || null,
                file: rec.file || null,
                requester: adminInfo?.name || null
            };
            const res = await fetch(`${API_URL}/receipts`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Не вдалося видалити квитанцію.');
            // Remove from local cache
            setReceiptsCache(prev => {
                const list = prev[name] || [];
                const filtered = list.filter(item => {
                    if (rec.file && item.file && item.file === rec.file) return false;
                    if (rec.tripleKey && item.tripleKey && item.tripleKey === rec.tripleKey) return false;
                    return true;
                });
                return { ...prev, [name]: filtered };
            });
        } catch (e) {
            setReceiptsError(e.message || 'Не вдалося видалити квитанцію.');
        }
    };

    return (
        <section className="admin-table-section">
            <h1>Таблиця Внесків</h1>
            <div className="admin-view-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button
                    onClick={() => setViewMode('contributions')}
                    className="admin-filter-select"
                    style={{ background: viewMode === 'contributions' ? '#782C36' : '#eee', color: viewMode === 'contributions' ? 'white' : '#333', cursor: 'pointer' }}
                >
                    Внески
                </button>
                <button
                    onClick={() => setViewMode('debt')}
                    className="admin-filter-select"
                    style={{ background: viewMode === 'debt' ? '#782C36' : '#eee', color: viewMode === 'debt' ? 'white' : '#333', cursor: 'pointer' }}
                >
                    Борг
                </button>
            </div>
            {viewMode === 'contributions' && (
            <>
            <div className="admin-filters">
                <select value={year} onChange={e => setYear(e.target.value)} className="admin-filter-select">
                    {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select value={kvartal} onChange={e => setKvartal(e.target.value)} className="admin-filter-select">
                    {availableKvartals.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
                {canDownloadZip && (
                    <button
                        onClick={handleDownloadZip}
                        className="admin-filter-select"
                        style={{ minWidth: '120px', cursor: isDownloadingZip ? 'wait' : 'pointer' }}
                        disabled={isDownloadingZip}
                    >
                        {isDownloadingZip ? 'Завантаження…' : 'DOWNLOAD'}
                    </button>
                )}
            </div>
            {downloadError && <p style={{ color: 'red' }}>{downloadError}</p>}
            {zipProgress && (
                <div className="zip-progress">
                    <div className="zip-progress__header">
                        <span>Архів: {zipProgress.label || `${year}_${kvartal}`}</span>
                        <span className={`zip-progress__status ${zipProgress.status || 'pending'}`}>
                            {getZipStatusLabel(zipProgress.status)}
                        </span>
                    </div>
                    <div className="zip-progress__bar">
                        <div
                            className="zip-progress__bar-fill"
                            style={{
                                width: `${(() => {
                                    if (!zipProgress) return 0;
                                    if (zipProgress.total && zipProgress.total > 0) {
                                        return Math.min(100, ((zipProgress.processed || 0) / zipProgress.total) * 100);
                                    }
                                    return zipProgress.status === 'completed' ? 100 : 0;
                                })()}%`
                            }}
                        />
                    </div>
                    <div className="zip-progress__meta">
                        <span>
                            {(zipProgress.processed || 0)}
                            {zipProgress.total ? ` / ${zipProgress.total}` : ''} файлів
                        </span>
                        {zipProgress.lastFile && (
                            <span className="zip-progress__file">Останній: {zipProgress.lastFile}</span>
                        )}
                        {zipProgress.error && (
                            <span className="zip-progress__error">{zipProgress.error}</span>
                        )}
                    </div>
                </div>
            )}
            {isLoading && <p>Завантаження даних...</p>}
            {error && <p style={{color: 'red'}}>Помилка: {error}</p>}
            {!isLoading && !error && (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr style={{ background: '#f2f2f2' }}>
                                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Ім'я</th>
                                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Курінна</th>
                                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Станична</th>
                                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Уладова</th>
                                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center', width: 70 }}>Avance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map(row => {
                                const isOpen = expandedName === row.name;
                                const receiptsForUser = receiptsCache[row.name] || [];
                                const sortedReceipts = [...receiptsForUser].sort((a, b) => {
                                    const aKey = String(a.storedAt || a.uploadedAt || '');
                                    const bKey = String(b.storedAt || b.uploadedAt || '');
                                    return bKey.localeCompare(aKey);
                                });
                                return (
                                    <React.Fragment key={row.name}>
                                        <tr>
                                            <td className="admin-name-cell" data-label="Ім'я">
                                                <button
                                                    onClick={() => handleToggleReceipts(row.name)}
                                                    className="admin-name-toggle"
                                                    aria-expanded={isOpen}
                                                >
                                                    <span className="admin-name-label">{row.name}</span>
                                                    <span className={`admin-name-arrow ${isOpen ? 'open' : ''}`}>▾</span>
                                                </button>
                                            </td>
                                            {['Курінна', 'Станична', 'Уладова'].map(type => (
                                                <td
                                                    key={type}
                                                    className={`status-cell ${row[type] === 'Сплачено' ? 'status-paid' : 'status-due'}`}
                                                    data-label={type}
                                                >
                                                    {row[type] === 'Сплачено' ? 'Сплачено' : `${row[type]} грн`}
                                                </td>
                                            ))}
                                            <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                {row.avanced ? <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '3px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>Avance</span> : <span style={{ color: '#ccc' }}>—</span>}
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="receipts-row">
                                                <td colSpan={5} className="receipts-cell">
                                                    {isReceiptsLoading ? (
                                                        <p>Завантаження квитанцій...</p>
                                                    ) : receiptsError ? (
                                                        <p className="receipts-error">{receiptsError}</p>
                                                    ) : sortedReceipts.length === 0 ? (
                                                        <p>Немає завантажених квитанцій для цього користувача.</p>
                                                    ) : (
                                                        <div className="receipts-list">
                                                            {sortedReceipts.map((rec, idx) => (
                                                                <div key={`${rec.receiptNo || 'receipt'}-${rec.storedAt || idx}`} className="receipt-card">
                                                                    <div className="receipt-meta">
                                                                        <span className="receipt-meta-item"><strong>ID:</strong> {rec.receiptNo || '—'}</span>
                                                                        <span className="receipt-meta-item"><strong>Тип:</strong> {rec.paymentType || '—'}</span>
                                                                        <span className="receipt-meta-item"><strong>Сума:</strong> {typeof rec.amount === 'number' ? `${rec.amount.toFixed(2)} грн` : '—'}</span>
                                                                        <span className="receipt-meta-item"><strong>Дата:</strong> {rec.date || '—'}</span>
                                                                    </div>
                                                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px' }}>
                                                                        {rec.url && (
                                                                            <a href={resolveReceiptUrl(rec.url)} target="_blank" rel="noreferrer noopener" className="receipt-link">
                                                                                Переглянути квитанцію
                                                                            </a>
                                                                        )}
                                                                        <button
                                                                            title="Видалити квитанцію"
                                                                            onClick={() => deleteReceipt(row.name, rec)}
                                                                            style={{
                                                                                background: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 10px',
                                                                                cursor: 'pointer', fontWeight: 600
                                                                            }}
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            </>
            )}

            {viewMode === 'debt' && (
                <div className="admin-debt-view">
                    <div className="admin-filters" style={{ flexWrap: 'wrap' }}>
                        <button
                            onClick={() => setDebtAll(true)}
                            className="admin-filter-select"
                            style={{ background: debtAll ? '#782C36' : '#eee', color: debtAll ? 'white' : '#333', cursor: 'pointer' }}
                        >
                            Увесь період
                        </button>
                        <button
                            onClick={() => setDebtAll(false)}
                            className="admin-filter-select"
                            style={{ background: !debtAll ? '#782C36' : '#eee', color: !debtAll ? 'white' : '#333', cursor: 'pointer' }}
                        >
                            Діапазон кварталів
                        </button>
                    </div>
                    {!debtAll && (
                        <div className="admin-filters" style={{ flexWrap: 'wrap', marginTop: '10px' }}>
                            <span style={{ alignSelf: 'center', fontWeight: 600 }}>З:</span>
                            <select value={debtFromYear} onChange={e => setDebtFromYear(parseInt(e.target.value, 10))} className="admin-filter-select">
                                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <select value={debtFromKvartal} onChange={e => setDebtFromKvartal(e.target.value)} className="admin-filter-select">
                                {allKvartalsList.map(k => <option key={k} value={k}>{k}</option>)}
                            </select>
                            <span style={{ alignSelf: 'center', fontWeight: 600 }}>По:</span>
                            <select value={debtToYear} onChange={e => setDebtToYear(parseInt(e.target.value, 10))} className="admin-filter-select">
                                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <select value={debtToKvartal} onChange={e => setDebtToKvartal(e.target.value)} className="admin-filter-select">
                                {allKvartalsList.map(k => <option key={k} value={k}>{k}</option>)}
                            </select>
                        </div>
                    )}
                    {debtLoading && <p>Обчислення боргу...</p>}
                    {debtError && <p style={{ color: 'red' }}>{debtError}</p>}
                    {!debtLoading && !debtError && debtData && (
                        <>
                            <p style={{ fontWeight: 600 }}>
                                Борг за період: {debtData.range.from.year} {debtData.range.from.kvartal} — {debtData.range.to.year} {debtData.range.to.kvartal}
                                {' '} | Загальна сума: <span style={{ color: '#c0392b' }}>{debtData.totalDebt.toFixed(2)} грн</span>
                            </p>
                            <div className="admin-table-wrapper">
                                <table className="admin-table">
                                    <thead>
                                        <tr style={{ background: '#f2f2f2' }}>
                                            <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Ім'я</th>
                                            <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Борг</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {debtData.users.length === 0 && (
                                            <tr><td colSpan={2} style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>Боргів немає.</td></tr>
                                        )}
                                        {debtData.users.map(u => {
                                            const isOpen = expandedDebtName === u.name;
                                            const keys = Object.keys(u.breakdown);
                                            return (
                                                <React.Fragment key={u.name}>
                                                    <tr>
                                                        <td className="admin-name-cell" data-label="Ім'я">
                                                            <button
                                                                onClick={() => toggleDebtUser(u.name)}
                                                                className="admin-name-toggle"
                                                                aria-expanded={isOpen}
                                                            >
                                                                <span className="admin-name-label">{u.name}</span>
                                                                <span className={`admin-name-arrow ${isOpen ? 'open' : ''}`}>▾</span>
                                                            </button>
                                                        </td>
                                                        <td className="status-cell status-due" data-label="Борг" style={{ fontWeight: 600 }}>
                                                            {u.debt.toFixed(2)} грн
                                                        </td>
                                                    </tr>
                                                    {isOpen && (
                                                        <tr className="receipts-row">
                                                            <td colSpan={2} className="receipts-cell">
                                                                <div className="receipts-list">
                                                                    {keys.map(key => {
                                                                        const [yy, kk] = key.split('-');
                                                                        return (
                                                                            <div key={key} className="receipt-card" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                                <span>{yy} {allKvartalsList[Number(kk)]}</span>
                                                                                <strong>{u.breakdown[key].toFixed(2)} грн</strong>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
        </section>
    );
};

// --- Admin settings helpers (level 2) ---

const getIdSortValue = (item) => {
    if (!item || typeof item !== 'object') return null;
    const idCandidate = item._id ?? item.id ?? item.ID;
    if (idCandidate === undefined || idCandidate === null) return null;

    if (typeof idCandidate === 'number' && Number.isFinite(idCandidate)) {
        return { type: 'number', value: idCandidate };
    }

    if (typeof idCandidate === 'string') {
        const trimmed = idCandidate.trim();
        if (!trimmed) return null;
        if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
            return { type: 'number', value: Number(trimmed) };
        }
        return { type: 'string', value: trimmed };
    }

    if (typeof idCandidate === 'object' && typeof idCandidate.$oid === 'string') {
        return { type: 'string', value: idCandidate.$oid.trim() };
    }

    return null;
};

const compareIdSortValues = (left, right) => {
    if (left.type === right.type) {
        if (left.type === 'number') return left.value - right.value;
        return left.value.localeCompare(right.value, 'uk', { numeric: true, sensitivity: 'base' });
    }
    return left.type === 'number' ? -1 : 1;
};

const formatJsonWithSortedIds = (value) => {
    if (Array.isArray(value)) {
        const formattedItems = value.map(formatJsonWithSortedIds);
        const sortable = formattedItems.every(item => getIdSortValue(item));
        if (!sortable) return formattedItems;
        return [...formattedItems].sort((a, b) => compareIdSortValues(getIdSortValue(a), getIdSortValue(b)));
    }

    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((acc, key) => {
            acc[key] = formatJsonWithSortedIds(value[key]);
            return acc;
        }, {});
    }

    return value;
};

let rowKeySeq = 0;
const nextRowKey = () => `row-${++rowKeySeq}`;

// A document is held as one text buffer per field so a half-typed value never destroys the row.
// String fields are edited verbatim; anything else round-trips through JSON.
const documentToRow = (doc) => {
    const fields = {};
    const kinds = {};
    Object.keys(doc || {}).forEach(key => {
        if (key === '_id') return;
        const value = doc[key];
        if (typeof value === 'string') {
            fields[key] = value;
            kinds[key] = 'string';
        } else {
            fields[key] = JSON.stringify(value);
            kinds[key] = 'json';
        }
    });
    return {
        key: nextRowKey(),
        id: doc && doc._id !== undefined && doc._id !== null ? String(doc._id) : null,
        fields,
        kinds,
        // fields the stored document actually had: an emptied new field is dropped, an
        // emptied existing field is kept as an empty value
        original: Object.keys(fields),
        isNew: !doc || doc._id === undefined || doc._id === null
    };
};

const rowToDocument = (row, columns) => {
    const out = {};
    columns.forEach(col => {
        const raw = row.fields[col];
        if (raw === undefined) return;
        const kind = row.kinds[col];
        const existed = row.original.includes(col);
        if (kind === 'string') {
            if (raw === '' && !existed) return;
            out[col] = raw;
            return;
        }
        if (kind === 'json') {
            if (raw.trim() === '' && !existed) return;
            try {
                out[col] = JSON.parse(raw);
            } catch (err) {
                throw new Error(`Поле "${col}": невалідний JSON.`);
            }
            return;
        }
        // Field added in the editor: JSON when it parses, plain text otherwise.
        if (raw === '') return;
        try {
            out[col] = JSON.parse(raw);
        } catch (err) {
            out[col] = raw;
        }
    });
    return out;
};

const collectColumns = (rows) => {
    const seen = [];
    rows.forEach(row => {
        Object.keys(row.fields).forEach(key => {
            if (!seen.includes(key)) seen.push(key);
        });
    });
    return seen;
};

// Admin settings for level 2: view/edit JSON files and Mongo collections.
// Collections are edited row by row (one document per row); _id is assigned by the server,
// never typed by hand. Raw JSON mode stays available for bulk edits.
const AdminSettings = ({ adminInfo }) => {
    const [overview, setOverview] = useState({ jsonFiles: [], mongoCollections: [], excluded: '' });
    const [overviewLoading, setOverviewLoading] = useState(false);
    const [overviewError, setOverviewError] = useState('');
    const [selectedSource, setSelectedSource] = useState(null); // { type: 'json' | 'collection', name: string }
    const [editorValue, setEditorValue] = useState('');
    const [editorLoading, setEditorLoading] = useState(false);
    const [editorError, setEditorError] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const [activeList, setActiveList] = useState('json');
    const [rows, setRows] = useState([]);
    const [editMode, setEditMode] = useState('rows'); // collections only: 'rows' | 'json'
    const [newColumn, setNewColumn] = useState('');
    const [busyRowKey, setBusyRowKey] = useState(null);

    const isLevel2 = Number(adminInfo?.level) === 2;
    const isCollection = selectedSource?.type === 'collection';
    const showRows = isCollection && editMode === 'rows';
    const columns = collectColumns(rows);

    const fetchOverview = async () => {
        if (!adminInfo?.name || !API_URL) return;
        setOverviewLoading(true);
        setOverviewError('');
        try {
            const params = new URLSearchParams({ requester: adminInfo.name });
            const res = await fetch(`${API_URL}/admin/settings/overview?${params.toString()}`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Не вдалося отримати дані.');
            setOverview({
                jsonFiles: Array.isArray(data.jsonFiles) ? data.jsonFiles : [],
                mongoCollections: Array.isArray(data.mongoCollections) ? data.mongoCollections : [],
                excluded: data.excluded || ''
            });
        } catch (err) {
            setOverviewError(err.message || 'Не вдалося отримати дані.');
        } finally {
            setOverviewLoading(false);
        }
    };

    useEffect(() => {
        if (isLevel2) {
            fetchOverview();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo?.name, isLevel2]);

    const applyDocuments = (documents) => {
        const list = Array.isArray(documents) ? documents : [];
        setRows(list.map(documentToRow));
        setEditorValue(JSON.stringify(list, null, 2));
    };

    const loadSource = async (type, name) => {
        if (!adminInfo?.name || !API_URL) return;
        setSelectedSource({ type, name });
        setEditorLoading(true);
        setEditorError('');
        setSaveMessage('');
        setEditorValue('');
        setRows([]);
        setNewColumn('');
        setEditMode('rows');
        try {
            const params = new URLSearchParams({ requester: adminInfo.name });
            if (type === 'json') {
                params.append('file', name);
                const res = await fetch(`${API_URL}/admin/settings/json?${params.toString()}`);
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.message || 'Не вдалося отримати файл.');
                setEditorValue(JSON.stringify(data.data ?? {}, null, 2));
            } else {
                params.append('collection', name);
                const res = await fetch(`${API_URL}/admin/settings/collection?${params.toString()}`);
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.message || 'Не вдалося отримати колекцію.');
                applyDocuments(data.documents);
            }
        } catch (err) {
            setEditorError(err.message || 'Не вдалося завантажити дані.');
            setEditorValue('');
            setRows([]);
        } finally {
            setEditorLoading(false);
        }
    };

    const reloadCollection = async () => {
        if (!selectedSource || selectedSource.type !== 'collection') return;
        await loadSource('collection', selectedSource.name);
    };

    const updateField = (rowKey, column, value) => {
        setRows(prev => prev.map(row => (
            row.key === rowKey ? { ...row, fields: { ...row.fields, [column]: value } } : row
        )));
    };

    const addRow = () => {
        const blank = documentToRow({});
        blank.fields = columns.reduce((acc, col) => { acc[col] = ''; return acc; }, {});
        setRows(prev => [...prev, blank]);
        setSaveMessage('');
        setEditorError('');
    };

    const addColumn = () => {
        const name = newColumn.trim();
        if (!name || name === '_id') {
            setEditorError('Вкажіть назву поля (не "_id").');
            return;
        }
        if (columns.includes(name)) {
            setEditorError('Таке поле вже існує.');
            return;
        }
        setRows(prev => prev.map(row => ({ ...row, fields: { ...row.fields, [name]: '' } })));
        setNewColumn('');
        setEditorError('');
    };

    const saveRow = async (row) => {
        if (!adminInfo?.name || !API_URL || !selectedSource) return;
        setEditorError('');
        setSaveMessage('');
        let document;
        try {
            document = rowToDocument(row, columns);
        } catch (err) {
            setEditorError(err.message);
            return;
        }
        setBusyRowKey(row.key);
        try {
            const res = await fetch(`${API_URL}/admin/settings/collection/document`, {
                method: row.id ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    requester: adminInfo.name,
                    collection: selectedSource.name,
                    ...(row.id ? { id: row.id } : {}),
                    document
                })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Не вдалося зберегти документ.');
            const saved = documentToRow(data.document || {});
            setRows(prev => prev.map(item => (item.key === row.key ? { ...saved, key: row.key } : item)));
            setSaveMessage(data.message || 'Документ збережено.');
        } catch (err) {
            setEditorError(err.message || 'Не вдалося зберегти документ.');
        } finally {
            setBusyRowKey(null);
        }
    };

    const deleteRow = async (row) => {
        if (!row.id) {
            setRows(prev => prev.filter(item => item.key !== row.key));
            return;
        }
        if (!window.confirm(`Видалити документ ${row.id}? Дію не можна скасувати.`)) return;
        setEditorError('');
        setSaveMessage('');
        setBusyRowKey(row.key);
        try {
            const params = new URLSearchParams({
                requester: adminInfo.name,
                collection: selectedSource.name,
                id: row.id
            });
            const res = await fetch(`${API_URL}/admin/settings/collection/document?${params.toString()}`, { method: 'DELETE' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Не вдалося видалити документ.');
            setRows(prev => prev.filter(item => item.key !== row.key));
            setSaveMessage(data.message || 'Документ видалено.');
        } catch (err) {
            setEditorError(err.message || 'Не вдалося видалити документ.');
        } finally {
            setBusyRowKey(null);
        }
    };

    const rowsToDocuments = () => rows.map(row => {
        const doc = rowToDocument(row, columns);
        return row.id ? { _id: row.id, ...doc } : doc;
    });

    const handleSave = async () => {
        if (!selectedSource || !adminInfo?.name || !API_URL) return;
        setEditorError('');
        setSaveMessage('');

        let payload;
        if (showRows) {
            try {
                payload = JSON.stringify(rowsToDocuments());
            } catch (err) {
                setEditorError(err.message);
                return;
            }
        } else {
            let parsed;
            try {
                parsed = JSON.parse(editorValue);
            } catch (err) {
                setEditorError(`Невалідний JSON: ${err.message}`);
                return;
            }
            if (isCollection && !Array.isArray(parsed)) {
                setEditorError('Для колекції очікується масив документів.');
                return;
            }
            payload = editorValue;
        }

        setEditorLoading(true);
        try {
            const body = selectedSource.type === 'json'
                ? { requester: adminInfo.name, file: selectedSource.name, content: payload }
                : { requester: adminInfo.name, collection: selectedSource.name, documents: payload };
            const res = await fetch(`${API_URL}/admin/settings/${selectedSource.type === 'json' ? 'json' : 'collection'}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Не вдалося зберегти.');
            setSaveMessage(data.message || 'Збережено.');
            if (data.documents) {
                applyDocuments(data.documents);
            } else if (data.data) {
                setEditorValue(JSON.stringify(data.data, null, 2));
            }
        } catch (err) {
            setEditorError(err.message || 'Не вдалося зберегти.');
        } finally {
            setEditorLoading(false);
        }
    };

    const handleFormat = () => {
        try {
            const parsed = JSON.parse(editorValue);
            const formatted = formatJsonWithSortedIds(parsed);
            setEditorValue(JSON.stringify(formatted, null, 2));
            setEditorError('');
        } catch (err) {
            setEditorError(`Невалідний JSON: ${err.message}`);
        }
    };

    // Switching modes carries the current edits over, so neither view goes stale.
    const switchMode = (mode) => {
        if (mode === editMode) return;
        setEditorError('');
        setSaveMessage('');
        if (mode === 'json') {
            try {
                setEditorValue(JSON.stringify(rowsToDocuments(), null, 2));
            } catch (err) {
                setEditorError(err.message);
                return;
            }
        } else {
            let parsed;
            try {
                parsed = JSON.parse(editorValue);
            } catch (err) {
                setEditorError(`Невалідний JSON: ${err.message}`);
                return;
            }
            if (!Array.isArray(parsed)) {
                setEditorError('Для колекції очікується масив документів.');
                return;
            }
            setRows(parsed.map(documentToRow));
        }
        setEditMode(mode);
    };

    if (!isLevel2) {
        return <div style={{ maxWidth: 700, margin: '20px auto' }}>Ця сторінка доступна лише адміністраторам 2 рівня.</div>;
    }

    const listButtonStyle = (active) => ({
        width: '100%',
        textAlign: 'left',
        padding: '10px 12px',
        border: 'none',
        background: active ? '#f3e5e9' : 'transparent',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0'
    });

    const cellStyle = { border: '1px solid #eee', padding: '4px', verticalAlign: 'top' };
    const inputStyle = { width: '100%', minWidth: 120, padding: '6px', border: '1px solid #ddd', borderRadius: 4, fontFamily: 'inherit', fontSize: '13px', boxSizing: 'border-box' };

    return (
        <section style={{ maxWidth: '1200px', margin: '0 auto', background: '#fff', borderRadius: '8px', border: '1px solid #eee', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0 }}>Налаштування (JSON / Mongo)</h2>
                <button onClick={fetchOverview} disabled={overviewLoading} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', background: overviewLoading ? '#ddd' : '#f7f7f7', cursor: overviewLoading ? 'not-allowed' : 'pointer' }}>
                    {overviewLoading ? 'Оновлення...' : 'Оновити списки'}
                </button>
            </div>
            {overviewError && <p style={{ color: 'red' }}>{overviewError}</p>}
            <div style={{ display: 'flex', gap: '20px', marginTop: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px', minWidth: 220 }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <button onClick={() => setActiveList('json')} style={{ flex: 1, padding: '8px', borderRadius: 6, border: '1px solid #ccc', background: activeList === 'json' ? '#782C36' : '#f7f7f7', color: activeList === 'json' ? '#fff' : '#333' }}>JSON файли</button>
                        <button onClick={() => setActiveList('collection')} style={{ flex: 1, padding: '8px', borderRadius: 6, border: '1px solid #ccc', background: activeList === 'collection' ? '#782C36' : '#f7f7f7', color: activeList === 'collection' ? '#fff' : '#333' }}>Колекції</button>
                    </div>
                    <div style={{ border: '1px solid #eee', borderRadius: 8, maxHeight: 320, overflowY: 'auto' }}>
                        {activeList === 'json' ? (
                            overview.jsonFiles.length === 0 ? (
                                <p style={{ padding: 12, margin: 0, color: '#777' }}>Немає JSON файлів (окрім {overview.excluded || 'receipts.json'}).</p>
                            ) : (
                                overview.jsonFiles.map(file => (
                                    <button key={file} onClick={() => loadSource('json', file)} style={listButtonStyle(selectedSource?.type === 'json' && selectedSource?.name === file)}>
                                        {file}
                                    </button>
                                ))
                            )
                        ) : (
                            overview.mongoCollections.length === 0 ? (
                                <p style={{ padding: 12, margin: 0, color: '#777' }}>Немає колекцій MongoDB.</p>
                            ) : (
                                overview.mongoCollections.map(col => (
                                    <button key={col} onClick={() => loadSource('collection', col)} style={listButtonStyle(selectedSource?.type === 'collection' && selectedSource?.name === col)}>
                                        {col}
                                    </button>
                                ))
                            )
                        )}
                    </div>
                </div>
                <div style={{ flex: '3 1 520px', minWidth: 320 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        <strong>{selectedSource ? `${selectedSource.type === 'json' ? 'JSON' : 'Колекція'}: ${selectedSource.name}` : 'Оберіть файл або колекцію'}</strong>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {isCollection && (
                                <>
                                    <button onClick={() => switchMode('rows')} disabled={editorLoading} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', background: editMode === 'rows' ? '#782C36' : '#f7f7f7', color: editMode === 'rows' ? '#fff' : '#333', cursor: 'pointer' }}>Таблиця</button>
                                    <button onClick={() => switchMode('json')} disabled={editorLoading} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', background: editMode === 'json' ? '#782C36' : '#f7f7f7', color: editMode === 'json' ? '#fff' : '#333', cursor: 'pointer' }}>JSON</button>
                                    <button onClick={reloadCollection} disabled={editorLoading} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer' }}>Перезавантажити</button>
                                </>
                            )}
                            {!showRows && (
                                <button onClick={handleFormat} disabled={!selectedSource || editorLoading} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', cursor: selectedSource ? 'pointer' : 'not-allowed' }}>Форматувати</button>
                            )}
                            <button onClick={handleSave} disabled={!selectedSource || editorLoading} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#782C36', color: '#fff', cursor: selectedSource ? 'pointer' : 'not-allowed' }}>
                                {editorLoading ? 'Збереження...' : (showRows ? 'Зберегти все' : 'Зберегти')}
                            </button>
                        </div>
                    </div>
                    {editorError && <p style={{ color: 'red' }}>{editorError}</p>}
                    {saveMessage && <p style={{ color: 'green' }}>{saveMessage}</p>}

                    {showRows ? (
                        <div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                                <button onClick={addRow} disabled={editorLoading} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #782C36', background: '#fff', color: '#782C36', cursor: 'pointer' }}>+ Документ</button>
                                <input
                                    value={newColumn}
                                    onChange={e => setNewColumn(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') addColumn(); }}
                                    placeholder="назва нового поля"
                                    style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: '13px' }}
                                />
                                <button onClick={addColumn} disabled={editorLoading} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', cursor: 'pointer' }}>+ Поле</button>
                                <span style={{ color: '#777', fontSize: '13px' }}>Документів: {rows.length}. _id створюється автоматично.</span>
                            </div>
                            <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ background: '#faf7f8' }}>
                                            <th style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>_id</th>
                                            {columns.map(col => (
                                                <th key={col} style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>{col}</th>
                                            ))}
                                            <th style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>Дії</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length === 0 && (
                                            <tr><td colSpan={columns.length + 2} style={{ ...cellStyle, color: '#777', padding: 12 }}>Порожня колекція.</td></tr>
                                        )}
                                        {rows.map(row => (
                                            <tr key={row.key}>
                                                <td style={{ ...cellStyle, whiteSpace: 'nowrap', fontFamily: 'monospace', color: row.id ? '#555' : '#999' }} title={row.id || ''}>
                                                    {row.id ? `${row.id.slice(0, 8)}…` : 'авто'}
                                                </td>
                                                {columns.map(col => {
                                                    const value = row.fields[col] ?? '';
                                                    const multiline = row.kinds[col] === 'json' && value.length > 40;
                                                    return (
                                                        <td key={col} style={cellStyle}>
                                                            {multiline ? (
                                                                <textarea
                                                                    value={value}
                                                                    onChange={e => updateField(row.key, col, e.target.value)}
                                                                    style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 60, resize: 'vertical' }}
                                                                />
                                                            ) : (
                                                                <input
                                                                    value={value}
                                                                    onChange={e => updateField(row.key, col, e.target.value)}
                                                                    style={{ ...inputStyle, fontFamily: row.kinds[col] === 'json' ? 'monospace' : 'inherit' }}
                                                                />
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                                                    <button onClick={() => saveRow(row)} disabled={busyRowKey === row.key || editorLoading} style={{ padding: '4px 8px', marginRight: 6, borderRadius: 4, border: 'none', background: '#782C36', color: '#fff', cursor: 'pointer' }}>
                                                        {busyRowKey === row.key ? '...' : 'Зберегти'}
                                                    </button>
                                                    <button onClick={() => deleteRow(row)} disabled={busyRowKey === row.key || editorLoading} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #c0392b', background: '#fff', color: '#c0392b', cursor: 'pointer' }}>
                                                        Видалити
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <textarea
                            value={editorValue}
                            onChange={e => setEditorValue(e.target.value)}
                            disabled={editorLoading || !selectedSource}
                            style={{ width: '100%', minHeight: '360px', fontFamily: 'monospace', fontSize: '14px', padding: '12px', borderRadius: 8, border: '1px solid #ccc', background: editorLoading ? '#f9f9f9' : '#fff', resize: 'vertical', boxSizing: 'border-box' }}
                            placeholder="Оберіть файл або колекцію, щоб побачити вміст"
                        />
                    )}
                </div>
            </div>
        </section>
    );
};

// Admin send page: select user, year, kvartal, payment type and upload a receipt on their behalf
const AdminSend = ({ adminInfo, names }) => {
    const [search, setSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [selYear, setSelYear] = useState(() => getCurrentYear());
    const [selKvartal, setSelKvartal] = useState(() => getCurrentKvartalName());
    const [paymentType, setPaymentType] = useState('Курінна');
    const [file, setFile] = useState(null);
    const [msg, setMsg] = useState('');
    const [uploading, setUploading] = useState(false);

    const allowedSource = Number(adminInfo?.level) === 1
        ? (names || []).filter(p => (adminInfo?.allowedNames || []).includes(p.name))
        : (names || []);
    const filtered = allowedSource.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

    // Fetch config to drive available years and kvartals similar to AdminTable
    const [config, setConfig] = useState(null);
    const [availableYears, setAvailableYears] = useState([]);
    const [availableKvartals, setAvailableKvartals] = useState([]);

    useEffect(() => {
        if (!API_URL) return;
        fetch(`${API_URL}/config`).then(r => r.json()).then(setConfig).catch(() => {});
    }, []);

    useEffect(() => {
        if (!config) return;
        const currentYearVal = getCurrentYear();
        const years = Array.from({ length: currentYearVal - config.startYear + 1 }, (_, i) => config.startYear + i);
        setAvailableYears(years);
    }, [config]);

    useEffect(() => {
        if (!config) return;
        const overriddenCurrent = getCurrentKvartalName(config);
        setSelKvartal(overriddenCurrent);
        const kvs = getAvailableKvartalsForYear(selYear, config);
        setAvailableKvartals(kvs);
    }, [config]);

    useEffect(() => {
        if (!config) return;
        const kvs = getAvailableKvartalsForYear(selYear, config);
        setAvailableKvartals(kvs);
        if (!kvs.includes(selKvartal) && kvs.length) {
            setSelKvartal(kvs[kvs.length - 1]);
        }
    }, [selYear]);

    // Build a representative date (YYYY-MM-DD) from year + kvartal to help backend if OCR misses date
    const dateFromYearKvartal = (y, kv, cfg) => {
        const yNum = parseInt(y, 10);
        const kvIndex = parseKvartalIndex(kv);
        // Check config override
        if (cfg?.kvartalOverrides && kvIndex !== null) {
            const key = `${String(yNum)}-${kvIndex}`;
            const override = cfg.kvartalOverrides[key];
            if (override?.start) {
                const parts = override.start.split('.');
                if (parts.length === 3) {
                    let yy = parts[2];
                    if (yy.length === 2) yy = '20' + yy;
                    return `${yNum}-${parts[1]}-${parts[0]}`;
                }
            }
        }
        // Default mid-quarter date
        let m = 1;
        if (kv === 'І квартал') m = 1;
        else if (kv === 'ІІ квартал') m = 4;
        else if (kv === 'ІІІ квартал') m = 7;
        else m = 10;
        const mm = String(m + 1).padStart(2, '0');
        const dd = '15';
        return `${yNum}-${mm}-${dd}`;
    };

    const handleSubmit = async () => {
        try {
            setMsg('');
            if (!adminInfo) { setMsg('Інформація про адміністратора недоступна.'); return; }
            const lvl = Number(adminInfo.level);
            if (![1,2].includes(lvl)) { setMsg('Ця дія доступна лише адміністраторам.'); return; }
            if (!selectedUser) { setMsg('Оберіть користувача.'); return; }
            if (!selYear || !selKvartal) { setMsg('Оберіть рік та квартал.'); return; }
            if (!file) { setMsg('Додайте файл квитанції.'); return; }
            const pilga = selectedUser?.pilga;
            if (!pilga) { setMsg('Не знайдено пільгу обраного користувача.'); return; }

            if (lvl === 1) {
                const allowed = Array.isArray(adminInfo.allowedNames) ? adminInfo.allowedNames : [];
                if (!allowed.includes(selectedUser.name)) {
                    setMsg('Вам не дозволено надсилати за цього користувача.');
                    return;
                }
            }

            setUploading(true);
            const form = new FormData();
            form.append('file', file);
            form.append('username', selectedUser.name);
            form.append('year', String(selYear));
            form.append('kvartal', selKvartal);
            form.append('pilga', pilga);
            form.append('paymentType', paymentType);
            form.append('requester', adminInfo.name);
            form.append('date', dateFromYearKvartal(selYear, selKvartal, config)); // server will normalize
            const res = await fetch(`${API_URL}/upload`, { method: 'POST', body: form });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(payload.message || 'Не вдалося надіслати квитанцію.');
            setMsg('Готово! Квитанцію надіслано.');
            setFile(null);
            setTimeout(() => setMsg(''), 2000);
        } catch (e) {
            setMsg(e.message || 'Сталася помилка.');
        } finally {
            setUploading(false);
        }
    };

    if (!adminInfo) {
        return <div style={{ maxWidth: 600, margin: '20px auto' }}>Інформація про адміністратора недоступна.</div>;
    }
    if (![1, 2].includes(Number(adminInfo.level))) {
        return <div style={{ maxWidth: 600, margin: '20px auto' }}>Ця сторінка доступна лише адміністраторам.</div>;
    }

    return (
        <section style={{ maxWidth: '700px', margin: '0 auto', background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #eee' }}>
            <h2 style={{ marginTop: 0 }}>Надіслати квитанцію від імені користувача</h2>
            <div style={{ display: 'grid', gap: '12px' }}>
                <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Знайти користувача</label>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Введіть ім'я та прізвище" style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6 }} />
                    {search && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                            {filtered.map(person => (
                                <li key={person.name}>
                                    <button onClick={() => { setSelectedUser(person); setSearch(person.name); }} style={{ width: '100%', textAlign: 'left', padding: 10, border: 'none', background: '#f9f9f9', cursor: 'pointer' }}>{person.name}</button>
                                </li>
                            ))}
                            {filtered.length === 0 && <li style={{ padding: 10, color: '#777' }}>Нічого не знайдено</li>}
                        </ul>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: 6 }}>Рік</label>
                        <select value={selYear} onChange={e => setSelYear(parseInt(e.target.value, 10))} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 6 }}>
                            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: 6 }}>Квартал</label>
                        <select value={selKvartal} onChange={e => setSelKvartal(e.target.value)} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 6 }}>
                            {availableKvartals.map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Тип внеску</label>
                    <select value={paymentType} onChange={e => setPaymentType(e.target.value)} style={{ padding: 10, border: '1px solid #ddd', borderRadius: 6 }}>
                        {['Курінна', 'Станична', 'Уладова'].map(pt => <option key={pt} value={pt}>{pt}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: 6 }}>Файл квитанції</label>
                    <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} />
                </div>
                <div>
                    <button onClick={handleSubmit} disabled={uploading || !selectedUser || !file} style={{ background: '#782C36', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer' }}>{uploading ? 'Надсилання...' : 'Надіслати'}</button>
                    {msg && <span style={{ marginLeft: 10 }}>{msg}</span>}
                </div>
                {selectedUser && (
                    <div style={{ fontSize: 12, color: '#555' }}>
                        Буде збережено для: <strong>{selectedUser.name}</strong>; Рік: <strong>{selYear}</strong>; Квартал: <strong>{selKvartal}</strong>; Пільга: <strong>{selectedUser.pilga}</strong>
                    </div>
                )}
            </div>
        </section>
    );
};

const AdminAvance = ({ adminInfo, names }) => {
    const [search, setSearch] = useState('');
    const [count, setCount] = useState(1);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    const allowedSource = Number(adminInfo?.level) === 1
        ? (names || []).filter(p => (adminInfo?.allowedNames || []).includes(p.name))
        : (names || []);

    const filtered = allowedSource.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase())
    );

    const setAvance = async () => {
        if (!selectedUser || !count || count < 1) return;
        setLoading(true);
        setMsg('');
        try {
            const res = await fetch(`${API_URL}/admin/avance/set`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    requester: adminInfo.name,
                    username: selectedUser.name,
                    count: Number(count)
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed');
            setMsg(`✅ ${data.message}`);
            setSelectedUser(null);
            setSearch('');
            setCount(1);
        } catch (err) {
            setMsg(`Помилка: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const removeAllAvance = async () => {
        if (!selectedUser) return;
        setLoading(true);
        setMsg('');
        try {
            const res = await fetch(`${API_URL}/admin/avance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    requester: adminInfo.name,
                    username: selectedUser.name,
                    year: '0',
                    kvartal: '0',
                    setAvance: false
                })
            });
            if (!res.ok) throw new Error('Failed');
            const colRes = await fetch(`${API_URL}/admin/settings/collection?requester=${encodeURIComponent(adminInfo.name)}&collection=names`);
            const colData = await colRes.json();
            const docs = Array.isArray(colData.documents) ? colData.documents : [];
            const doc = docs.find(d => d.name === selectedUser.name);
            if (doc) {
                const updateRes = await fetch(`${API_URL}/admin/settings/collection`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        requester: adminInfo.name,
                        collection: 'names',
                        documents: docs.map(d => d.name === selectedUser.name ? { ...d, avance: [] } : d)
                    })
                });
                const updateData = await updateRes.json();
                if (!updateRes.ok) throw new Error(updateData.message || 'Failed');
            }
            setMsg(`✅ Avance повністю знято для ${selectedUser.name}`);
            setSelectedUser(null);
            setSearch('');
        } catch (err) {
            setMsg(`Помилка: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const existingAvance = selectedUser?.avance && Array.isArray(selectedUser.avance)
        ? selectedUser.avance.map(k => {
            const [y, i] = k.split('-');
            return `${KVARTAL_NAMES[Number(i)] || '?'} ${y}`;
          }).join(', ')
        : 'немає';

    return (
        <section style={{ maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 8, border: '1px solid #eee', padding: 20 }}>
            <h2 style={{ marginTop: 0 }}>Встановити Avance</h2>
            <p style={{ color: '#555', marginBottom: 16 }}>Avance — звільнення від оплати на вказану кількість кварталів (починаючи з поточного).</p>

            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder="Пошук користувача..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setSelectedUser(null); }}
                    style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', flex: '1 1 200px' }}
                />
                <input
                    type="number"
                    min="1"
                    value={count}
                    onChange={e => setCount(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', width: 80 }}
                    placeholder="К-сть"
                />
                <span style={{ color: '#666', fontSize: 14 }}>кварталів</span>
            </div>

            {!selectedUser && filtered.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12, maxHeight: 300, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, padding: 4 }}>
                    {filtered.map(user => (
                        <button
                            key={user.name}
                            onClick={() => { setSelectedUser(user); setSearch(user.name); }}
                            style={{
                                textAlign: 'left', padding: '8px 12px', border: 'none', borderRadius: 4,
                                background: '#f7f7f7', cursor: 'pointer', fontSize: 14
                            }}
                        >
                            {user.name}
                        </button>
                    ))}
                </div>
            )}

            {msg && <p style={{ color: msg.startsWith('Помилка') ? 'red' : '#2e7d32', margin: '8px 0', fontWeight: 500 }}>{msg}</p>}

            {selectedUser && (
                <div style={{ background: '#fafafa', borderRadius: 6, border: '1px solid #eee', padding: 16 }}>
                    <p style={{ fontWeight: 600, margin: '0 0 8px' }}>{selectedUser.name}</p>
                    <p style={{ fontSize: 13, color: '#555', margin: '0 0 4px' }}>Поточний Avance: <strong>{existingAvance}</strong></p>
                    <p style={{ fontSize: 13, color: '#555', margin: '0 0 16px' }}>Встановити на <strong>{count}</strong> кварталів починаючи з поточного.</p>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button
                            onClick={setAvance}
                            disabled={loading}
                            style={{
                                padding: '10px 20px', borderRadius: 6, border: 'none',
                                background: '#782C36', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                                fontWeight: 600, fontSize: 15
                            }}
                        >
                            {loading ? 'Зачекайте...' : 'Встановити Avance'}
                        </button>
                        <button
                            onClick={removeAllAvance}
                            disabled={loading}
                            style={{
                                padding: '10px 20px', borderRadius: 6, border: '1px solid #d32f2f',
                                background: '#fff', color: '#d32f2f', cursor: loading ? 'not-allowed' : 'pointer',
                                fontWeight: 600, fontSize: 15
                            }}
                        >
                            Зняти всі Avance
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

// The main App component is heavily modified to handle the user selection flow.
function App() {
    const [isAppLoading, setIsAppLoading] = useState(true); // NEW: State for initial app load
    const [names, setNames] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [selectedName, setSelectedName] = useState(null);
    const [pendingUser, setPendingUser] = useState(null); // користувач, для якого показуємо парольну форму
    const [authMode, setAuthMode] = useState(null); // 'login' | 'register' | null
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [pwd1, setPwd1] = useState('');
    const [pwd2, setPwd2] = useState('');
    const [adminInfo, setAdminInfo] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [currentPage, setCurrentPage] = useState('current');
    const [debts, setDebts] = useState([]);
    const [openDebtKey, setOpenDebtKey] = useState(null);
    const [currentYear] = useState(new Date().getFullYear());
    const [currentKvartal, setCurrentKvartal] = useState(getCurrentKvartalName);
    const [config, setConfig] = useState(null);
    // Manual signup flow (when user types but no matches found)
    const [showManualSignup, setShowManualSignup] = useState(false);
    const [regLastName, setRegLastName] = useState('');
    const [regFirstName, setRegFirstName] = useState('');
    const [regPwd1, setRegPwd1] = useState('');
    const [regPwd2, setRegPwd2] = useState('');
    const [regError, setRegError] = useState('');
    const [regLoading, setRegLoading] = useState(false);
    const [regExists, setRegExists] = useState(null); // null = unknown, true = exists in users.json, false = not exists
    const [regChecking, setRegChecking] = useState(false);

    // Debounced check against users.json via /auth/check-user when entering last+first name
    useEffect(() => {
        if (!showManualSignup) { setRegExists(null); return; }
        const ln = regLastName.trim();
        const fn = regFirstName.trim();
        if (!ln || !fn) { setRegExists(null); return; }
        const fullName = `${ln} ${fn}`.replace(/\s+/g, ' ').trim();
        let cancelled = false;
        setRegChecking(true);
        const t = setTimeout(async () => {
            try {
                const res = await fetch(`${API_URL}/auth/check-user?username=${encodeURIComponent(fullName)}`);
                const data = await res.json().catch(() => ({}));
                if (!cancelled) setRegExists(!!data.exists);
            } catch (_) {
                if (!cancelled) setRegExists(null);
            } finally {
                if (!cancelled) setRegChecking(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [showManualSignup, regLastName, regFirstName]);

    useEffect(() => {
        if (!API_URL) {
            console.error("API_URL is not defined.");
            setIsAppLoading(false);
            return;
        }

        console.log("Fetching data from:", API_URL);

        const fetchInitialData = async () => {
            try {
                console.log("Attempting to fetch names and admins...");
                const [namesRes, adminsRes, configRes] = await Promise.all([
                    fetch(`${API_URL}/names`),
                    fetch(`${API_URL}/admins`),
                    fetch(`${API_URL}/config`)
                ]);
                
                console.log("Response statuses:", { names: namesRes.status, admins: adminsRes.status, config: configRes.status });
                
                if (!namesRes.ok || !adminsRes.ok) {
                    throw new Error(`HTTP error: names=${namesRes.status}, admins=${adminsRes.status}`);
                }
                
                const namesRaw = await namesRes.json();
                const adminsData = await adminsRes.json();
                const configData = await configRes.json().catch(() => null);
                const namesData = normalizeNamesPayload(namesRaw);
                
                console.log("Data fetched successfully:", { namesCount: namesData.length, adminsCount: adminsData.length });
                
                setNames(namesData);
                setAdmins(adminsData);
                setConfig(configData);
                if (configData) setCurrentKvartal(getCurrentKvartalName(configData));

                // After fetching data, check for a saved user in cookies
                const savedUser = Cookies.get('selectedUser');
                if (savedUser) {
                    try {
                        const user = JSON.parse(savedUser);
                        // Make sure the user from cookie still exists in the list
                        if (namesData.some(n => n.name === user.name)) {
                           handleNameSelect(user, true);
                        }
                    } catch (e) { console.error("Could not parse user from cookie", e); Cookies.remove('selectedUser');}
                }
            } catch (error) {
                console.error("Failed to fetch initial app data:", error);
                // Set empty data so the app doesn't stay stuck
                setNames([]);
                setAdmins([]);
            } finally {
                setIsAppLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!selectedName || admins.length === 0) {
            setAdminInfo(null);
            return;
        }
        const adminData = admins.find(admin => admin.name === selectedName.name);
        setAdminInfo(adminData || null);
    }, [selectedName, admins]);

    useEffect(() => {
        if (!selectedName || !API_URL) return;
        const fetchDebts = async () => {
            try {
                const debtRes = await fetch(`${API_URL}/debts/${encodeURIComponent(selectedName.name)}`);
                setDebts(await debtRes.json());
            } catch (error) { console.error("Could not fetch debts", error); }
        };
        fetchDebts();
    }, [selectedName]);

    const handleNameSelect = async (person, fromCookie = false) => {
        setAuthError('');
        setPwd1('');
        setPwd2('');
        setPendingUser(person);
        if (fromCookie) {
            // Користувач уже авторизований раніше
            setSelectedName(person);
            return;
        }
        try {
            const res = await fetch(`${API_URL}/auth/check-user?username=${encodeURIComponent(person.name)}`);
            const data = await res.json();
            setAuthMode(data.exists ? 'login' : 'register');
        } catch (e) {
            setAuthError('Не вдалося перевірити користувача. Спробуйте пізніше.');
        }
    };

    const submitRegister = async () => {
        if (!pendingUser) return;
        setAuthLoading(true);
        setAuthError('');
        try {
            const res = await fetch(`${API_URL}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: pendingUser.name, password: pwd1, confirm: pwd2 })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Помилка реєстрації');
            Cookies.set('selectedUser', JSON.stringify(pendingUser), { expires: 30 });
            setSelectedName(pendingUser);
            setPendingUser(null);
            setAuthMode(null);
        } catch (e) {
            setAuthError(e.message);
        } finally {
            setAuthLoading(false);
        }
    };

    const submitLogin = async () => {
        if (!pendingUser) return;
        setAuthLoading(true);
        setAuthError('');
        try {
            const res = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: pendingUser.name, password: pwd1 })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Помилка входу');
            Cookies.set('selectedUser', JSON.stringify(pendingUser), { expires: 30 });
            setSelectedName(pendingUser);
            setPendingUser(null);
            setAuthMode(null);
        } catch (e) {
            setAuthError(e.message);
        } finally {
            setAuthLoading(false);
        }
    };

    const handleUserChange = () => {
        Cookies.remove('selectedUser');
        setSelectedName(null);
        setAdminInfo(null);
        setCurrentPage('current');
        setInputValue(''); // Reset input field
    };
    
    const handleToggleDebt = (key) => setOpenDebtKey(prevKey => (prevKey === key ? null : key));

    // Render a loading screen for the whole app
    if (isAppLoading) {
        return <div style={{ textAlign: 'center', padding: '50px', fontSize: '24px' }}>Завантаження додатку...</div>;
    }

    // NEW: Render the User Selection screen if no user is selected
    if (!selectedName) {
        const q = inputValue.trim().toLowerCase();
        const filteredNames = q
            ? names.filter(person => person.name.toLowerCase().includes(q)).slice(0, 3)
            : [];

        return (
            <div style={{ maxWidth: '500px', margin: '40px auto', padding: '20px', fontFamily: 'sans-serif' }}>
                <h1 style={{ textAlign: 'center', color: '#333' }}>Виберіть користувача</h1>
                {names.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        <p>Не вдалося завантажити список користувачів.</p>
                        <p>Перевірте, чи запущений сервер на порту 4000.</p>
                        <p>API URL: {API_URL}</p>
                        <button 
                            onClick={() => window.location.reload()} 
                            style={{ padding: '10px 20px', marginTop: '10px', background: '#782C36', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                            Спробувати знову
                        </button>
                    </div>
                ) : (
                    <>
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Введіть ваше прізвище та ім'я..."
                            style={{ width: '100%', padding: '12px', fontSize: '16px', marginBottom: '12px', boxSizing: 'border-box' }}
                        />
                        {q && filteredNames.length === 1 && (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '300px', overflowY: 'auto' }}>
                                {filteredNames.map(person => (
                                    <li key={person.name}>
                                        <button 
                                            onClick={() => handleNameSelect(person)}
                                            style={{ width: '100%', padding: '12px', marginBottom: '8px', border: '1px solid #ddd', background: '#f9f9f9', textAlign: 'left', cursor: 'pointer', fontSize: '16px' }}
                                        >
                                            {person.name}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {q && filteredNames.length === 0 && (
                            <div style={{ marginTop: '8px', color: '#666' }}>
                                <p style={{ margin: '8px 0' }}>Нічого не знайдено.</p>
                                <p style={{ margin: '8px 0' }}>
                                    Вашого імені немає у списку. Будь ласка, напишіть на
                                    {' '}<a href="mailto:dvorakbodion020@gmail.com">dvorakbodion020@gmail.com</a>{' '}для додавання.
                                </p>
                                {!showManualSignup ? (
                                    <button onClick={() => { setShowManualSignup(true); setRegError(''); }} style={{ padding: '8px 12px', background: '#782C36', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                        Зареєструватися
                                    </button>
                                ) : (
                                    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px', background: '#fff', padding: '12px', border: '1px solid #ddd', borderRadius: '6px' }}>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <input type="text" placeholder="Прізвище" value={regLastName} onChange={(e) => setRegLastName(e.target.value)} style={{ flex: 1, padding: '10px', fontSize: '16px' }} />
                                            <input type="text" placeholder="Ім'я" value={regFirstName} onChange={(e) => setRegFirstName(e.target.value)} style={{ flex: 1, padding: '10px', fontSize: '16px' }} />
                                        </div>
                                        <input type="password" placeholder="Пароль" value={regPwd1} onChange={(e) => setRegPwd1(e.target.value)} style={{ padding: '10px', fontSize: '16px' }} />
                                        <input type="password" placeholder="Повторіть пароль" value={regPwd2} onChange={(e) => setRegPwd2(e.target.value)} style={{ padding: '10px', fontSize: '16px' }} />
                                        {regChecking && <p style={{ color: '#777', margin: 0 }}>Перевірка...</p>}
                                        {regExists === true && <p style={{ color: '#c0392b', margin: 0 }}>Користувач уже зареєстрований. Знайдіть своє ім'я у списку та увійдіть.</p>}
                                        {regError && <p style={{ color: 'red', margin: 0 }}>{regError}</p>}
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {regExists === false && (
                                            <button onClick={async () => {
                                                try {
                                                    setRegError('');
                                                    const fullName = `${regLastName.trim()} ${regFirstName.trim()}`.replace(/\s+/g, ' ').trim();
                                                    if (!regLastName.trim() || !regFirstName.trim()) { setRegError('Вкажіть прізвище та ім\'я.'); return; }
                                                    if (regPwd1.length < 6) { setRegError('Пароль має містити не менше 6 символів.'); return; }
                                                    if (regPwd1 !== regPwd2) { setRegError('Паролі не співпадають.'); return; }
                                                    const person = names.find(n => n.name === fullName);
                                                    if (!person) {
                                                        setRegError('Вашого імені немає у списку.');
                                                        return;
                                                    }
                                                    // Re-check users.json to ensure not already registered
                                                    try {
                                                        const checkRes = await fetch(`${API_URL}/auth/check-user?username=${encodeURIComponent(fullName)}`);
                                                        const check = await checkRes.json().catch(() => ({}));
                                                        if (check.exists) { setRegError('Користувач уже зареєстрований. Спробуйте увійти.'); return; }
                                                    } catch (_) {}
                                                    setRegLoading(true);
                                                    const res = await fetch(`${API_URL}/auth/register`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({ username: fullName, password: regPwd1, confirm: regPwd2 })
                                                    });
                                                    const data = await res.json().catch(() => ({}));
                                                    if (!res.ok) throw new Error(data.message || 'Помилка реєстрації');
                                                    Cookies.set('selectedUser', JSON.stringify(person), { expires: 30 });
                                                    setSelectedName(person);
                                                    setShowManualSignup(false);
                                                    setRegLastName(''); setRegFirstName(''); setRegPwd1(''); setRegPwd2('');
                                                } catch (e) {
                                                    setRegError(e.message || 'Помилка реєстрації');
                                                } finally {
                                                    setRegLoading(false);
                                                }
                                            }} disabled={regLoading} style={{ background: '#782C36', color: 'white', padding: '10px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{regLoading ? 'Реєстрація...' : 'Зареєструватись'}</button>
                                            )}
                                            <button onClick={() => { setShowManualSignup(false); setRegError(''); }} style={{ padding: '10px 16px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>Скасувати</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {pendingUser && authMode && (
                            <div style={{ marginTop: '20px', padding: '16px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff' }}>
                                <h3 style={{ marginTop: 0 }}>Підтвердження користувача: {pendingUser.name}</h3>
                                {authMode === 'register' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <input type="password" placeholder="Створіть пароль" value={pwd1} onChange={(e) => setPwd1(e.target.value)} style={{ padding: '10px', fontSize: '16px' }} />
                                        <input type="password" placeholder="Повторіть його" value={pwd2} onChange={(e) => setPwd2(e.target.value)} style={{ padding: '10px', fontSize: '16px' }} />
                                        {authError && <p style={{ color: 'red', margin: 0 }}>{authError}</p>}
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button onClick={submitRegister} disabled={authLoading} style={{ background: '#782C36', color: 'white', padding: '10px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{authLoading ? 'Реєстрація...' : 'Зберегти пароль'}</button>
                                            <button onClick={() => { setPendingUser(null); setAuthMode(null); }} style={{ padding: '10px 16px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>Скасувати</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <input type="password" placeholder="Пароль" value={pwd1} onChange={(e) => setPwd1(e.target.value)} style={{ padding: '10px', fontSize: '16px' }} />
                                        {authError && <p style={{ color: 'red', margin: 0 }}>{authError}</p>}
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button onClick={submitLogin} disabled={authLoading} style={{ background: '#782C36', color: 'white', padding: '10px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{authLoading ? 'Вхід...' : 'Увійти'}</button>
                                            <button onClick={() => { setPendingUser(null); setAuthMode(null); }} style={{ padding: '10px 16px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>Скасувати</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }
    
    // Render the main app interface ONLY when a user has been selected
    return (
        <div style={{ fontFamily: 'sans-serif' }}>
            <header style={{ background: '#782C36', padding: '15px 20px', color: 'white', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ul style={{ display: 'flex', gap: '20px', padding: 0, listStyle: 'none', margin: 0 }}>
                        <li><button onClick={() => setCurrentPage('current')} style={{ background: 'none', border: 'none', textDecoration: 'none', color: 'white', fontWeight: currentPage === 'current' ? 'bold' : 'normal', cursor: 'pointer', fontSize: '16px' }}>Теперішній квартал</button></li>
                        <li><button onClick={() => setCurrentPage('debts')} style={{ background: 'none', border: 'none', textDecoration: 'none', color: 'white', fontWeight: currentPage === 'debts' ? 'bold' : 'normal', cursor: 'pointer', fontSize: '16px' }}>Борги {debts.length > 0 && `(${debts.length})`}</button></li>
                        {adminInfo && <li><button onClick={() => setCurrentPage('table')} style={{ background: 'none', border: 'none', textDecoration: 'none', color: 'white', fontWeight: currentPage === 'table' ? 'bold' : 'normal', cursor: 'pointer', fontSize: '16px' }}>Таблиця</button></li>}
                        {adminInfo && [1,2].includes(Number(adminInfo.level)) && (
                            <li><button onClick={() => setCurrentPage('send')} style={{ background: 'none', border: 'none', textDecoration: 'none', color: 'white', fontWeight: currentPage === 'send' ? 'bold' : 'normal', cursor: 'pointer', fontSize: '16px' }}>Надіслати (адмін)</button></li>
                        )}
                        {adminInfo && [1,2].includes(Number(adminInfo.level)) && (
                            <li><button onClick={() => setCurrentPage('avance')} style={{ background: 'none', border: 'none', textDecoration: 'none', color: 'white', fontWeight: currentPage === 'avance' ? 'bold' : 'normal', cursor: 'pointer', fontSize: '16px' }}>Аванс</button></li>
                        )}
                        {adminInfo && Number(adminInfo.level) === 2 && (
                            <li><button onClick={() => setCurrentPage('settings')} style={{ background: 'none', border: 'none', textDecoration: 'none', color: 'white', fontWeight: currentPage === 'settings' ? 'bold' : 'normal', cursor: 'pointer', fontSize: '16px' }}>Налаштування</button></li>
                        )}
                        {/* Видалено пункт "Зворотня комунікація" за вимогою */}
                    </ul>
                    <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                        <button onClick={handleUserChange} style={{ padding: '6px 12px', cursor: 'pointer', background: 'transparent', border: '1px solid white', color: 'white', borderRadius: '4px' }}>Змінити користувача.</button>
                    </div>
                </div>
            </header>
            
            <main style={{ padding: '20px' }}>
                {currentPage === 'current' && <section style={{ maxWidth: '400px', margin: '0 auto' }}><h2>Оплата за: {currentKvartal}, {currentYear}</h2><PaymentInterface user={selectedName} year={currentYear} kvartal={currentKvartal} /></section>}
                {currentPage === 'table' && adminInfo && <AdminTable adminInfo={adminInfo} names={names} />}
                {currentPage === 'send' && adminInfo && <AdminSend adminInfo={adminInfo} names={names} />}
                {currentPage === 'avance' && adminInfo && <AdminAvance adminInfo={adminInfo} names={names} />}
                {currentPage === 'settings' && adminInfo && Number(adminInfo.level) === 2 && (
                    <AdminSettings adminInfo={adminInfo} />
                )}
                {currentPage === 'debts' && (
                    <section style={{ maxWidth: '400px', margin: '0 auto' }}>
                        <h1>Борги</h1>
                        {debts.length === 0 ? <p>У вас немає боргів.</p> : (
                            <ul style={{ display: 'flex', flexDirection: 'column', gap: '15px', listStyle: 'none', padding: 0 }}>
                                {debts.map(debt => {
                                    const debtKey = `${debt.year}-${debt.kvartal}`;
                                    const isOpen = openDebtKey === debtKey;
                                    return (
                                        <li key={debtKey} style={{ background: '#fff8f8', border: '1px solid #e0b4b4', padding: '15px 20px', borderRadius: '8px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleToggleDebt(debtKey)}>
                                                <div><strong style={{ fontSize: '1.2em' }}>{debt.kvartal}, {debt.year}</strong><p style={{ margin: '5px 0 0', fontSize: '1.1em', color: '#c0392b' }}>Борг: <strong>{debt.amountOwed.toFixed(2)} грн</strong></p></div>
                                                <div style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 10L12 15L17 10" stroke="#782C36" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                                            </div>
                                            {isOpen && <PaymentInterface user={selectedName} year={debt.year} kvartal={debt.kvartal} />}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                )}
            </main>
        </div>
    );
}

export default App;
